import React, { useCallback, useState } from 'react';
import Select, {
  components,
  type MenuListProps,
  type OptionProps,
  type SingleValueProps,
} from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import { RuntimeOptionContent } from './runtimeCardHelpers';
import type { ClusterOption } from './utils/clusterOptions';
import { NEXLA_CLOUD_VALUE } from './utils/clusterOptions';
import { runtimeSelectStyles } from './runtimeSelectStyles';
import styles from './RuntimeSelection.module.css';

export interface RuntimeSelectionInteractiveProps {
  options: ClusterOption[];
  selectedClusterId: string | number | null | undefined;
  onSelectRuntime: (clusterId: number | string | null | undefined) => void;
}

// react-select registers closeMenuOnScroll on `document` in the CAPTURE phase,
// so it fires for scrolls of ANY element — including the widget's own hidden
// search input, which browsers auto-scroll while typing to keep the caret
// visible. Only scrolls outside the control and the portaled menu may close
// the fixed-position menu.
export function shouldCloseMenuOnScroll(event: Event): boolean {
  return !(
    event.target instanceof Element &&
    event.target.closest('.runtimeSelect__control, .runtimeSelect__menu') != null
  );
}

function RuntimeOption(props: OptionProps<ClusterOption, false>) {
  const { data, isDisabled, innerProps } = props;
  return (
    <components.Option
      {...props}
      innerProps={{ ...innerProps, title: isDisabled ? data.disabledMessage : undefined }}
    >
      <RuntimeOptionContent option={data} />
    </components.Option>
  );
}

function RuntimeSingleValue(props: SingleValueProps<ClusterOption, false>) {
  return (
    <components.SingleValue {...props}>
      <RuntimeOptionContent option={props.data} />
    </components.SingleValue>
  );
}

// Purely decorative: mirrors `inputValue` so search appears to live inside the
// menu. Typing always happens on react-select's own (visually hidden) input,
// which already holds real focus whenever the menu is open - rendering this as
// a real, independently focusable <input> risks react-select treating focus as
// having left the widget and closing the menu (menuListRef/blur interaction).
function RuntimeMenuList(props: MenuListProps<ClusterOption, false>) {
  const { selectProps, children } = props;
  const searchText = selectProps.inputValue;
  return (
    <components.MenuList {...props}>
      <div className={styles.menuSearchWrapper} aria-hidden="true">
        <SearchIcon fontSize="small" className={styles.menuSearchIcon} />
        {searchText ? (
          <span className={styles.menuSearchText}>{searchText}</span>
        ) : (
          <span className={styles.menuSearchPlaceholder}>Search runtime...</span>
        )}
      </div>
      {children}
    </components.MenuList>
  );
}

export default function RuntimeSelectionInteractive(props: RuntimeSelectionInteractiveProps) {
  const { options, selectedClusterId, onSelectRuntime } = props;
  const [searchText, setSearchText] = useState('');

  const resolvedSelectionValue =
    selectedClusterId == null ? NEXLA_CLOUD_VALUE : String(selectedClusterId);

  const selectedOption = options.find((option) => option.value === resolvedSelectionValue) ?? null;

  const handleSelect = useCallback(
    (option: ClusterOption | null) => {
      if (option == null || option.isDisabled === true) return;
      const value = option.value;
      const isNexlaCloud = value === NEXLA_CLOUD_VALUE;
      const parsed = parseInt(value, 10);
      const nextClusterId = isNexlaCloud ? null : Number.isNaN(parsed) ? value : parsed;
      onSelectRuntime(nextClusterId);
    },
    [onSelectRuntime],
  );

  return (
    <div className={styles.selectControl}>
      <Select<ClusterOption, false>
        aria-label="Runtime"
        classNamePrefix="runtimeSelect"
        value={selectedOption}
        onChange={handleSelect}
        options={options}
        isOptionDisabled={(option) => option.isDisabled === true}
        isClearable={false}
        isSearchable
        inputValue={searchText}
        onInputChange={(value, meta) => {
          if (meta.action === 'input-change') setSearchText(value);
        }}
        onMenuClose={() => setSearchText('')}
        menuPlacement="top"
        menuPosition="fixed"
        maxMenuHeight={500}
        minMenuHeight={300}
        closeMenuOnScroll={shouldCloseMenuOnScroll}
        menuPortalTarget={document.body}
        styles={runtimeSelectStyles}
        components={{
          Option: RuntimeOption,
          SingleValue: RuntimeSingleValue,
          MenuList: RuntimeMenuList,
        }}
      />
    </div>
  );
}
